import { DataType, IndexType, SystemGeneratedTableColumnType } from '@doss/prisma/types'
import { formatDateColumnValue } from './dates.js'
import { LocaleCode } from './locales.js'
import { NumberColumnFormat, formatNumberColumnValue } from './numbers.js'
import { isSystemGeneratedColumn } from './types/table-column.js'

/**
 * Checks if the column type can be used for the column with the lowest order on the table
 */
export function isValidFirstTableColumnType(type: DataType): boolean {
  switch (type) {
    case DataType.ENUM:
    case DataType.FORMULA_NUMBER:
    case DataType.FORMULA_STRING:
    case DataType.FORMULA_DATE:
    case DataType.FORMULA_DATE_TIME:
    case DataType.ID:
    case DataType.LOOKUP:
    case DataType.STRING:
    case DataType.NUMBER:
    case DataType.DATE:
    case DataType.DATE_TIME:
    case DataType.USER:
      return true
    case DataType.ARRAY:
    case DataType.BOOLEAN:
    case DataType.FILE_URL:
    case DataType.FORMULA_BOOLEAN:
      return false
  }
}

/**
 * Given the data type and index type of a column, checks if the column is numeric
 */
export function isNumericColumn(columnDataType: DataType, columnIndexType?: IndexType): boolean {
  return (
    columnDataType === DataType.NUMBER ||
    columnDataType === DataType.FORMULA_NUMBER ||
    (columnDataType === DataType.ID && columnIndexType === IndexType.NUMBER)
  )
}

/**
 * Given the data type of a column, checks if the column is a date column
 */
export function isDateColumn(columnDataType: DataType): boolean {
  return (
    columnDataType === DataType.DATE ||
    columnDataType === DataType.DATE_TIME ||
    columnDataType === DataType.FORMULA_DATE ||
    columnDataType === DataType.FORMULA_DATE_TIME
  )
}

/**
 * Given the data type and index type of a column, checks if the column is a text-based column
 */
export function isTextBasedColumn(columnDataType: DataType, columnIndexType?: IndexType): boolean {
  return (
    columnDataType === DataType.STRING ||
    columnDataType === DataType.ENUM ||
    columnDataType === DataType.FORMULA_STRING ||
    (columnDataType === DataType.ID && columnIndexType === IndexType.STRING)
  )
}

/**
 * Given the data type of a column, checks if the column is a formula column.
 */
export function isFormulaColumnType(columnDataType: DataType): boolean {
  return (
    columnDataType === DataType.FORMULA_NUMBER ||
    columnDataType === DataType.FORMULA_STRING ||
    columnDataType === DataType.FORMULA_DATE ||
    columnDataType === DataType.FORMULA_DATE_TIME ||
    columnDataType === DataType.FORMULA_BOOLEAN
  )
}

/**
 * Exclude formula columns from the CSV template that is downloaded.
 */
export function isReadOnlyColumn(columnDataType: DataType, indexAutogenerate: boolean): boolean {
  return (
    isFormulaColumnType(columnDataType) || (columnDataType === DataType.ID && indexAutogenerate)
  )
}

/**
 * Given a column, checks if the edit permission of the column is required to have the
 * ability to add a row to the table
 */
export function isEditPermissionRequiredForAddRow(
  columnDataType: DataType,
  indexAutogenerate: boolean,
  isNullable: boolean,
): boolean {
  return !isNullable && !isReadOnlyColumn(columnDataType, indexAutogenerate)
}

/**
 * Returns the system-generated column types from an array of tab columns. Use this when
 * the column type is a complex GraphQL union/intersection so that TypeScript does not
 * try to intersect with TableColumnWithSystemGeneratedType (which can reduce to never).
 */
export function getSystemGeneratedColumnTypesFromColumns(
  columns: { systemGeneratedColumnType?: string | null }[],
): SystemGeneratedTableColumnType[] {
  const result: SystemGeneratedTableColumnType[] = []
  for (const column of columns) {
    if (isSystemGeneratedColumn(column)) {
      result.push(column.systemGeneratedColumnType)
    }
  }
  return result
}

/**
 * Get the data type and index type for a system generated column
 */
export function getDataTypeForSystemGeneratedColumn(
  systemGeneratedColumnType: SystemGeneratedTableColumnType,
): { dataType: DataType; indexType: IndexType } {
  switch (systemGeneratedColumnType) {
    case SystemGeneratedTableColumnType.CREATED_AT:
    case SystemGeneratedTableColumnType.UPDATED_AT:
      return { dataType: DataType.DATE_TIME, indexType: IndexType.NONE }
    case SystemGeneratedTableColumnType.CREATED_BY:
    case SystemGeneratedTableColumnType.UPDATED_BY:
      return { dataType: DataType.USER, indexType: IndexType.NONE }
  }
}

export const SYSTEM_COLUMN_DISPLAY_NAME: Record<SystemGeneratedTableColumnType, string> = {
  [SystemGeneratedTableColumnType.CREATED_AT]: 'Created At',
  [SystemGeneratedTableColumnType.CREATED_BY]: 'Created By',
  [SystemGeneratedTableColumnType.UPDATED_AT]: 'Updated At',
  [SystemGeneratedTableColumnType.UPDATED_BY]: 'Updated By',
}

type DimensionColumnFormat = NumberColumnFormat & { timeZone?: string }

/**
 * Formats a single dimension value using the column's format config. Handles numbers
 * (with optional range splitting for histogram/interval buckets like "0 - 100") and
 * dates/date-times. Returns null for other column types so callers can fall back to the
 * raw value.
 */
export const formatDimensionValue = (
  value: string | number | Date,
  columnType: DataType | null | undefined,
  columnFormat: DimensionColumnFormat,
  locale: LocaleCode,
  { splitNumberRange = false }: { splitNumberRange?: boolean } = {},
): string | null => {
  if (columnType === DataType.NUMBER || columnType === DataType.FORMULA_NUMBER) {
    // Histogram/interval buckets arrive as a single "<min> - <max>" string,
    // so we format min/max individually and then rejoin them.
    // Unparseable values fall through to formatNumberColumnValue, which returns '' for them.
    const endpoints = typeof value === 'string' ? value.split(' - ') : []
    const isRange = splitNumberRange && endpoints.length === 2
    if (isRange) {
      return endpoints
        .map((endpoint) => formatNumberColumnValue(endpoint, columnFormat, locale))
        .join(' - ')
    }
    return formatNumberColumnValue(value, columnFormat, locale) || null
  }
  if (
    columnType === DataType.DATE ||
    columnType === DataType.DATE_TIME ||
    columnType === DataType.FORMULA_DATE ||
    columnType === DataType.FORMULA_DATE_TIME
  ) {
    return formatDateColumnValue(
      value,
      {
        ...columnFormat,
        isDateTime: columnType === DataType.DATE_TIME || columnType === DataType.FORMULA_DATE_TIME,
      },
      locale,
    )
  }
  return null
}
